import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  Paper,
  Button,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  ImageList,
  ImageListItem,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import BedIcon from '@mui/icons-material/Bed';
import BathtubIcon from '@mui/icons-material/Bathtub';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ApartmentIcon from '@mui/icons-material/Apartment';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import SingleBedIcon from '@mui/icons-material/SingleBed';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import SecurityIcon from '@mui/icons-material/Security';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CloseIcon from '@mui/icons-material/Close';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import { rentalAPI, userAPI } from '../../utils/api';
import { rentalDetailPath } from '../../utils/listingPaths';
import ReactImageGallery from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';
import MapView from '../../components/common/MapView';
import MatterportEmbed from '../../components/rental/MatterportEmbed';
import { USC_DPS_POLYGON } from '../../constants/uscDpsPolygon';
import './RentalDetail.css';
import { isAuthenticated, getCurrentUser } from '../../utils/auth';
import {
  formatBathrooms,
  formatBedroomBathroom,
  formatAvailabilityDate,
  formatBedroomsLabel,
  hasBedroomAndBathroomValues,
} from '../../utils/formatters';
import {
  getYouTubeID,
  getMatterportEmbedSrc,
  getMatterportModelId,
  getMatterportThumbUrl,
} from '../../utils/virtualTours';

const DEFAULT_PAGE_TITLE = 'BunnyRent - Smart Housing Solutions';

/** 与 YouTube 一致的 16:9 外框，并排时高度对齐 */
const tourMediaFrameSx = {
  position: 'relative',
  width: '100%',
  paddingBottom: '56.25%',
  height: 0,
  overflow: 'hidden',
  bgcolor: '#000',
};

function RentalDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [rental, setRental] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFavorite, setIsFavorite] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: "Hi, I'm interested in your property and would like to schedule a viewing or get more details. Please let me know your availability. Thank you!"
  });

  // 当联系对话框打开时，自动填写用户信息
  const handleOpenContactDialog = () => {
    if (isAuthenticated()) {
      const currentUser = getCurrentUser();
      if (currentUser) {
        const fullName = `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim();
        setContactForm(prev => ({
          ...prev,
          name: fullName || currentUser.email || '',
          email: currentUser.email || ''
        }));
      }
    }
    setContactDialogOpen(true);
  };
  const [mode, setMode] = useState('grid'); // 'grid' or 'carousel'
  const [carouselIndex, setCarouselIndex] = useState(0);
  /** 用户点击播放后才挂载 iframe（避免 Matterport / YouTube 自动加载） */
  const [activeVirtualTours, setActiveVirtualTours] = useState(() => new Set());

  useEffect(() => {
    setLoading(true);
    rentalAPI.getRental(slug)
      .then(res => setRental(res.data))
      .catch(() => setError('Failed to load rental detail'))
      .finally(() => setLoading(false));
  }, [slug]);

  useEffect(() => {
    setActiveVirtualTours(new Set());
  }, [slug]);

  const playVirtualTour = (key) => {
    setActiveVirtualTours((prev) => new Set(prev).add(key));
  };

  useEffect(() => {
    if (rental?.title) {
      document.title = `${rental.title} | BunnyRent`;
    }
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
    };
  }, [rental?.title]);

  // 旧链接或旧前端用数字 id 打开时，替换为 canonical slug URL
  useEffect(() => {
    if (!rental?.slug || !slug || slug === rental.slug) return;
    const isNumericId =
      slug === String(rental.id) ||
      (/^\d+$/.test(slug) && Number(slug) === Number(rental.id));
    if (isNumericId) {
      navigate(rentalDetailPath(rental), { replace: true });
    }
  }, [rental, slug, navigate]);

  // 检查当前房源是否已被收藏
  useEffect(() => {
    if (rental && isAuthenticated()) {
      userAPI.getFavorites()
        .then(res => {
          const favorites = res.data;
          const isFavorited = favorites.some(fav => fav.id === rental.id);
          setIsFavorite(isFavorited);
        })
        .catch(err => {
          console.error('Failed to check favorite status:', err);
        });
    }
  }, [rental]);

  // 修复：useEffect 必须在 return 之前
  // 保证当前主图在缩略图窗口内（如需自动滚动可在此实现）
  // 这里只是占位，实际 thumbStart 逻辑已在渲染中处理
  useEffect(() => {}, [carouselIndex, rental?.images?.length]);

  if (loading) return <div>Loading...</div>;
  if (error) return <div>{error}</div>;
  if (!rental) return null;

  const handleBack = () => {
    navigate(-1);
  };

  const handleToggleFavorite = async () => {
    if (!isAuthenticated()) {
      navigate('/auth', { state: { from: window.location.pathname } });
      return;
    }
    try {
      const res = await userAPI.toggleFavorite(rental.id);
      setIsFavorite(res.data.favorited);
    } catch (e) {
      // 错误处理
    }
  };

  // 根据租赁类型获取标题
  const getRentalTypeTitle = (type) => {
    switch (type) {
      case 'entire_unit':
        return 'Available Units';
      case 'by_room':
        return 'Available Rooms';
      case 'by_bed':
        return 'Available Beds';
      default:
        return 'Available Options';
    }
  };

  const getAvailableOptions = () => {
    if (!rental) return [];
    
    switch (rental.rental_type) {
      case 'entire':
        return rental.units?.filter(unit => unit.isAvailable) || [];
      case 'room':
        return rental.rooms?.filter(room => room.isAvailable) || [];
      case 'bed':
        return rental.beds?.filter(bed => bed.isAvailable) || [];
      default:
        return [];
    }
  };

  const availableOptions = getAvailableOptions();

  const handleSendMessage = (unit) => {
    // Implement the logic to send a message to the landlord
  };

  const handleContactInputChange = (event) => {
    const { name, value } = event.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCloseContactDialog = () => {
    setContactDialogOpen(false);
  };

  const handleSubmitContact = async () => {
    try {
      // 调用后端API发送消息
      const response = await rentalAPI.contactLandlord({
        rental_id: rental.id,
        name: contactForm.name,
        email: contactForm.email,
        phone: contactForm.phone,
        message: contactForm.message
      });
      
      // 发送Message成功后，自动创建Progress
      try {
        await userAPI.createProgress(rental.id);
      } catch (progressError) {
        // 如果是已存在的错误，静默忽略
        if (
          progressError.response &&
          progressError.response.data &&
          progressError.response.data.error === 'Progress already exists'
        ) {
          // do nothing
        } else {
          // 其它错误可以console报错
          console.error('Failed to create progress:', progressError);
        }
      }
      
      // 显示成功消息
      setSuccessDialogOpen(true);
      setContactDialogOpen(false);
      
      // 重置表单
      setContactForm({
        name: '',
        email: '',
        phone: '',
        message: "Hi, I'm interested in your property and would like to schedule a viewing or get more details. Please let me know your availability. Thank you!"
      });
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMsg = error.response?.data?.error || 'Failed to send message. Please try again.';
      setErrorMessage(errorMsg);
      setErrorDialogOpen(true);
    }
  };

  const getResponseTimeText = (responseTime) => {
    const responseTimeMap = {
      'within-same-day': 'Within same day',
      'within-1-day': 'Within 1 business day',
      'within-2-3-days': 'Within 2-3 business days',
      'within-1-week': 'Within 1 week'
    };
    return responseTimeMap[responseTime] || 'Within 2-3 business days';
  };

  // 图片数组
  const images = (rental.images || []).map(url => ({ original: url }));
  const total = images.length;

  // 宫格页：主图+4小图
  const gridImages = images.slice(0, 5);
  // 轮播页缩略图横向滚动窗口
  const thumbWindow = 8;
  let thumbStart = Math.max(0, carouselIndex - Math.floor(thumbWindow / 2));
  if (thumbStart + thumbWindow > total) thumbStart = Math.max(0, total - thumbWindow);
  const thumbEnd = Math.min(total, thumbStart + thumbWindow);
  const carouselThumbs = images.slice(thumbStart, thumbEnd);

  const isUnit = rental.rental_type === 'entire';
  const isRoom = rental.rental_type === 'room';
  const isBed = rental.rental_type === 'bed';

  const tableData = isUnit
    ? rental.units
    : isRoom
      ? rental.rooms
      : isBed
        ? rental.beds
        : [];

  const getNumber = (item) =>
    item.unitNumber || item.roomNumber || item.bedNumber || '-';

  const showSqft = isUnit || isRoom;
  const showBathroom = isRoom;

  const colCount = (showSqft ? 1 : 0) + (showBathroom ? 1 : 0) + 3;
  const colWidth = `${100 / colCount}%`;

  const isSpecialEntire =
    isUnit &&
    ['House', 'Townhouse', 'Condo'].includes(rental.property_type);

  const isApartmentEntire = isUnit && rental.property_type === 'Apartment';

  // 拦截未登录用户
  const handleMessageClick = () => {
    if (!isAuthenticated()) {
      navigate('/auth', { state: { from: window.location.pathname } });
      return;
    }
    handleOpenContactDialog();
  };

  /** 与找室友详情页一致：右侧 sticky「Contact Information」栏 */
  const renderContactSidebar = () => (
    <Grid item xs={12} md={4}>
      <Paper sx={{ p: 3, position: 'sticky', top: 20 }}>
        <Typography variant="h6" gutterBottom>
          Contact Information
        </Typography>
        <List>
          <ListItem>
            <ListItemText
              primary="Typical response time"
              secondary={getResponseTimeText(rental.contact_info?.responseTime)}
            />
          </ListItem>
          {rental.contact_info?.languages &&
          Array.isArray(rental.contact_info.languages) &&
          rental.contact_info.languages.length > 0 ? (
            <ListItem>
              <ListItemText
                primary="Languages spoken"
                secondary={rental.contact_info.languages.join(', ')}
              />
            </ListItem>
          ) : null}
        </List>
        <Button variant="contained" fullWidth size="large" sx={{ mt: 2 }} onClick={handleMessageClick}>
          Message Landlord
        </Button>
        <Button
          variant="outlined"
          fullWidth
          size="large"
          sx={{ mt: 1.5 }}
          onClick={handleToggleFavorite}
          startIcon={isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        >
          {isFavorite ? 'Saved' : 'Save to Favorites'}
        </Button>
      </Paper>
    </Grid>
  );

  const renderVirtualTours = () => {
    const youtubeIds = (rental.youtube_video_urls || [])
      .map((url) => getYouTubeID(url))
      .filter(Boolean);
    const matterportTours = (rental.matterport_urls || [])
      .map((url) => {
        const modelId = getMatterportModelId(url);
        const embedSrc = getMatterportEmbedSrc(url);
        if (!embedSrc || !modelId) return null;
        return {
          tourKey: `mp-${modelId}`,
          modelId,
          embedSrc,
          thumbUrl: getMatterportThumbUrl(modelId),
        };
      })
      .filter(Boolean);

    if (youtubeIds.length === 0 && matterportTours.length === 0) return null;

    const tourCount = youtubeIds.length + matterportTours.length;
    const mdCols = tourCount === 1 ? 12 : tourCount === 2 ? 6 : 4;

    return (
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Video & 3D Tours
        </Typography>
        <Grid container spacing={2}>
          {youtubeIds.map((id, index) => {
            const tourKey = `yt-${id}`;
            const isActive = activeVirtualTours.has(tourKey);
            return (
              <Grid item xs={12} md={mdCols} key={`${tourKey}-${index}`} sx={{ display: 'flex' }}>
                <Paper
                  elevation={1}
                  sx={{ overflow: 'hidden', borderRadius: 2, width: '100%' }}
                >
                  <Box sx={tourMediaFrameSx}>
                    {isActive ? (
                      <iframe
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          border: 0,
                        }}
                        src={`https://www.youtube.com/embed/${id}?autoplay=1`}
                        title={`YouTube video ${index + 1}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    ) : (
                      <Box
                        role="button"
                        tabIndex={0}
                        onClick={() => playVirtualTour(tourKey)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') playVirtualTour(tourKey);
                        }}
                        sx={{
                          position: 'absolute',
                          inset: 0,
                          cursor: 'pointer',
                          backgroundImage: `url(https://img.youtube.com/vi/${id}/hqdefault.jpg)`,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center',
                          '&:hover .tour-play-overlay': { bgcolor: 'rgba(0,0,0,0.45)' },
                        }}
                      >
                        <Box
                          className="tour-play-overlay"
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 1,
                            bgcolor: 'rgba(0,0,0,0.35)',
                            transition: 'background-color 0.2s',
                          }}
                        >
                          <PlayCircleOutlineIcon sx={{ fontSize: 72, color: '#fff' }} />
                          <Typography variant="subtitle1" sx={{ color: '#fff', fontWeight: 600 }}>
                            Play video
                          </Typography>
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Paper>
              </Grid>
            );
          })}
          {matterportTours.map((tour, index) => (
            <Grid item xs={12} md={mdCols} key={tour.tourKey} sx={{ display: 'flex' }}>
              <Paper
                elevation={1}
                sx={{ overflow: 'hidden', borderRadius: 2, width: '100%' }}
              >
                <Box sx={tourMediaFrameSx}>
                  <MatterportEmbed
                    embedSrc={tour.embedSrc}
                    thumbUrl={tour.thumbUrl}
                    modelId={tour.modelId}
                    title={`Matterport 3D tour ${index + 1}`}
                  />
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  // 宫格页
  if (mode === 'grid') {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header Section */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
        >
          Back to Listings
        </Button>
        <IconButton onClick={handleToggleFavorite} color="primary">
          {isFavorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
        </IconButton>
      </Box>

      {/* Property Title and Type */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Chip
              label={rental.property_type}
            variant="outlined"
          />
          <Chip
              label={rental.rental_type === 'entire' ? 'Entire Property' : 
                     rental.rental_type === 'room' ? 'By Room' : 'By Bed'}
            variant="outlined"
            color="secondary"
          />
        </Stack>
        <Typography variant="h4" gutterBottom>
          {rental.title}
        </Typography>
        <Typography variant="h6" color="text.secondary">
            {rental.address_line1}
            {rental.address_line2 ? `, ${rental.address_line2}` : ''}, 
            {rental.city}, {rental.state} {rental.zip_code}
        </Typography>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={8}>
        {/* 图片宫格或缩略图页 */}
        <Box sx={{ mb: 4 }}>
          <Grid container spacing={1}>
            <Grid item xs={8}>
              <Box
                className="rental-gallery-main"
                sx={{ position: 'relative', width: '100%', height: 400, borderRadius: 2, overflow: 'hidden', cursor: 'pointer' }}
                onClick={() => { setMode('carousel'); setCarouselIndex(0); }}
              >
                <img
                  src={images[0]?.original || 'https://source.unsplash.com/random/800x600/?apartment'}
                  alt="Main"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }}
                />
                {/* 右下角箭头 */}
                {total > 5 && (
                  <Box className="rental-gallery-arrow-mask" onClick={e => { e.stopPropagation(); setMode('carousel'); setCarouselIndex(0); }}>
                    <ChevronRightIcon sx={{ fontSize: 32, mr: 1 }} />
                    {total} Photos
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={4}>
              <Grid container spacing={1} direction="column">
                {gridImages.slice(1).map((img, idx) => (
                  <Grid item xs={12} key={idx}>
                    <Box
                      className="rental-gallery-thumb"
                      sx={{ width: '100%', height: 95, borderRadius: 2, overflow: 'hidden', position: 'relative', cursor: 'pointer' }}
                      onClick={() => { setMode('carousel'); setCarouselIndex(idx + 1); }}
                    >
                      <img
                        src={img.original}
                        alt={`Small ${idx + 1}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                      />
                    </Box>
                  </Grid>
                ))}
                {/* 不足4张补空白 */}
                {Array.from({ length: Math.max(0, 4 - (gridImages.length - 1)) }).map((_, idx) => (
                  <Grid item xs={12} key={`empty-${idx}`}></Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Box>

        {/* YouTube Videos Section */}
        {renderVirtualTours()}

      {/* Property Overview */}
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          Property Overview
        </Typography>
        <Box sx={{ mt: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Address
            </Typography>
            <Typography variant="body1">
                {rental.address_line1}
                {rental.address_line2 && <>, {rental.address_line2}</>}
              <br />
                {rental.city}, {rental.state} {rental.zip_code}
            </Typography>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Lease Length Options
            </Typography>
            <Typography variant="body1">
                {rental.lease_lengths && Array.isArray(rental.lease_lengths) 
                  ? rental.lease_lengths.join(', ') 
                  : 'Flexible'}
            </Typography>
          </Box>

          <Box>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Security Deposit
            </Typography>
            <Typography variant="body1">
                {rental.security_deposit || 'Not specified'}
            </Typography>
          </Box>
        </Box>
      </Paper>

        {/* Description Section */}
        {rental.description && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {rental.description}
            </Typography>
          </Paper>
        )}

      {/* Promotions Section */}
        {rental.has_promotion && rental.promotion_text && (
        <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#fff3e0' }}>
          <Typography variant="h6" gutterBottom color="primary">
            Special Promotion
          </Typography>
          <Typography variant="body1">
              {rental.promotion_text}
          </Typography>
        </Paper>
      )}

        {/* Available Rooms/Units/Beds Section or Property Detail */}
        {isApartmentEntire ? (
      <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
              Available Units
            </Typography>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">Unit</TableCell>
                    <TableCell align="center">Bedrooms</TableCell>
                    <TableCell align="center">Bathrooms</TableCell>
                    <TableCell align="center">Sq Ft</TableCell>
                    <TableCell align="center">Price</TableCell>
                    <TableCell align="center">Availability</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rental.units && rental.units.length > 0 ? (
                    rental.units.map((unit, idx) => (
                      <TableRow key={unit.id || unit.unitNumber || idx}>
                        <TableCell align="left">{unit.unitNumber}</TableCell>
                        <TableCell align="center">{formatBedroomsLabel(unit.bedrooms)}</TableCell>
                        <TableCell align="center">{formatBathrooms(unit.bathrooms)}</TableCell>
                        <TableCell align="center">{unit.squareFeet || '-'}</TableCell>
                        <TableCell align="center">${unit.price}</TableCell>
                        <TableCell align="center">{formatAvailabilityDate(unit.availableDate)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : isSpecialEntire ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Property Detail
            </Typography>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ width: '20%' }}>Bedrooms</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Bathrooms</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Sq Ft</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Price</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Available Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">{formatBedroomsLabel(rental.bedrooms)}</TableCell>
                    <TableCell align="left">{formatBathrooms(rental.bathrooms)}</TableCell>
                    <TableCell align="left">{rental.square_feet || '-'}</TableCell>
                    <TableCell align="left">{'$' + (Number(rental.price) % 1 === 0 ? parseInt(rental.price) : rental.price)}</TableCell>
                    <TableCell align="left">{formatAvailabilityDate(rental.available_date)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {rental.rental_type === 'entire' ? 'Available Units' :
               rental.rental_type === 'room' ? 'Available Rooms' :
               'Available Beds'}
            </Typography>
        
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                    <TableCell align="center">{isUnit ? 'Unit' : isRoom ? 'Room' : 'Bed'}</TableCell>
                    <TableCell align="center">Base Price</TableCell>
                    {showSqft && <TableCell align="center">Sq Ft</TableCell>}
                    {showBathroom && <TableCell align="center">Bathroom</TableCell>}
                    <TableCell align="center">Availability</TableCell>
                    {(isRoom || isBed) && <TableCell align="center">Unit Type</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
                  {tableData && tableData.length > 0 ? (
                    tableData.map((item, idx) => (
                      <TableRow key={item.id || item.unitNumber || item.roomNumber || item.bedNumber || idx}>
                        <TableCell align="center">{getNumber(item)}</TableCell>
                        <TableCell align="center">${item.price}</TableCell>
                        {showSqft && <TableCell align="center">{item.squareFeet || '-'}</TableCell>}
                        {showBathroom && <TableCell align="center">{item.hasBathroom ? 'Private' : 'Shared'}</TableCell>}
                        <TableCell align="center">{formatAvailabilityDate(item.availableDate)}</TableCell>
                        {(isRoom || isBed) && (
                          rental && ['House', 'Townhouse', 'Condo'].includes(rental.property_type)
                            ? (
                                tableData.length > 1
                                  ? (idx === 0
                                      ? <TableCell align="center" rowSpan={tableData.length}>{formatBedroomBathroom(rental.bedrooms, rental.bathrooms)}</TableCell>
                                      : null)
                                  : <TableCell align="center">{formatBedroomBathroom(rental.bedrooms, rental.bathrooms)}</TableCell>
                              )
                            : <TableCell align="center">{hasBedroomAndBathroomValues(item.bedrooms, item.bathrooms) ? formatBedroomBathroom(item.bedrooms, item.bathrooms) : '-'}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={colCount + ((isRoom || isBed) ? 1 : 0)}>
                        No data
                      </TableCell>
                    </TableRow>
                  )}
            </TableBody>
          </Table>
        </TableContainer>
          </Paper>
        )}

        {/* Amenities */}
        {rental.amenities && Array.isArray(rental.amenities) && rental.amenities.length > 0 && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Amenities
            </Typography>
            <Grid container spacing={2}>
              {rental.amenities.map((amenity, index) => (
                <Grid item xs={12} sm={6} md={4} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon color="primary" />
                    <Typography>{amenity}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* 地图展示（移到Amenities后） */}
        {rental.latitude && rental.longitude && (
          <Box sx={{ height: 400, my: 4 }}>
            <MapView 
              centerLat={rental.latitude}
              centerLng={rental.longitude}
              markers={[{ id: rental.id, lat: rental.latitude, lng: rental.longitude }]}
              markerData={{ [rental.id]: rental }}
              allowBoundsChange={false}
              uscPolygon={USC_DPS_POLYGON}
            />
          </Box>
        )}
        </Grid>
        {renderContactSidebar()}
      </Grid>

        {/* Contact Form Dialog */}
        <Dialog
          open={contactDialogOpen}
          onClose={handleCloseContactDialog}
          maxWidth="sm"
          fullWidth
        >
              <DialogTitle>
            Contact Property Owner
                <IconButton
                  aria-label="close"
              onClick={handleCloseContactDialog}
                  sx={{ position: 'absolute', right: 8, top: 8 }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              <DialogContent>
            <Box sx={{ mt: 2 }}>
                <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Full Name *
                </Typography>
                <input
                  style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', backgroundColor: '#f5f5f5' }}
                  name="name"
                  type="text"
                  value={contactForm.name}
                  onChange={handleContactInputChange}
                  placeholder="Enter your full name"
                  autoComplete="new-password"
                  required
                  disabled
                />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Name is automatically filled from your account and cannot be changed
                </Typography>
                </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Email *
                </Typography>
                <input
                  style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
                  name="email"
                  type="email"
                  value={contactForm.email}
                  onChange={handleContactInputChange}
                  placeholder="Enter your email address"
                  autoComplete="new-password"
                  required
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Phone Number (Optional)
                </Typography>
                <input
                  style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
                  name="phone"
                  type="text"
                  value={contactForm.phone}
                  onChange={handleContactInputChange}
                  placeholder="Enter your phone number"
                  autoComplete="new-password"
                />
              </Box>
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Message *
                </Typography>
                <textarea
                  style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', minHeight: 80 }}
                  name="message"
                  value={contactForm.message}
                  onChange={handleContactInputChange}
                  placeholder="Write your message here..."
                  required
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Your message will be sent to the property owner through our secure system.
              </Typography>
            </Box>
              </DialogContent>
          <DialogActions sx={{ p: 3 }}>
            <Button onClick={handleCloseContactDialog} color="inherit">
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitContact} 
              variant="contained"
              disabled={!contactForm.name || !contactForm.email || !contactForm.message}
            >
              Send Message
            </Button>
          </DialogActions>
        </Dialog>

        {/* 成功提示弹窗 */}
        <Dialog open={successDialogOpen} onClose={() => setSuccessDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', color: 'success.main' }}>
            <CheckCircleIcon sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
            <Typography variant="h6" component="div">
              Message Sent Successfully!
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Your message has been sent to the landlord. They will get back to you soon!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We've also created a progress record for you to track your interest in this property.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={() => setSuccessDialogOpen(false)} 
              variant="contained" 
              color="primary"
              size="large"
            >
              Got it!
            </Button>
          </DialogActions>
        </Dialog>

        {/* 错误提示弹窗 */}
        <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ textAlign: 'center', color: 'error.main' }}>
            <Typography variant="h6" component="div">
              Error
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {errorMessage}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button 
              onClick={() => setErrorDialogOpen(false)} 
              variant="contained" 
              color="primary"
              size="large"
            >
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    );
  }

  // 轮播页
  if (mode === 'carousel') {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
        <Box sx={{ mb: 4, position: 'relative', bgcolor: '#fafbfc', borderRadius: 3, minHeight: 480, p: 0 }}>
          {/* 关闭按钮 */}
          <IconButton
            onClick={() => setMode('grid')}
            sx={{ position: 'absolute', top: 16, right: 24, zIndex: 10, bgcolor: 'rgba(255,255,255,0.85)' }}
          >
            <CloseIcon />
          </IconButton>
          {/* 左箭头 */}
          <IconButton
            onClick={() => setCarouselIndex(i => Math.max(0, i - 1))}
            disabled={carouselIndex === 0}
            sx={{ position: 'absolute', top: '50%', left: 16, zIndex: 10, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)' }}
          >
            <ChevronLeftIcon sx={{ fontSize: 40 }} />
          </IconButton>
          {/* 右箭头 */}
          <IconButton
            onClick={() => setCarouselIndex(i => Math.min(total - 1, i + 1))}
            disabled={carouselIndex === total - 1}
            sx={{ position: 'absolute', top: '50%', right: 16, zIndex: 10, transform: 'translateY(-50%)', bgcolor: 'rgba(255,255,255,0.85)' }}
          >
            <ChevronRightIcon sx={{ fontSize: 40 }} />
          </IconButton>
          {/* 大图 */}
          <Box sx={{ width: '100%', height: 480, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8f8f8', borderRadius: 3 }}>
            <img
              src={images[carouselIndex]?.original}
              alt={`Photo ${carouselIndex + 1}`}
              style={{ maxWidth: '100%', maxHeight: 460, borderRadius: 12, objectFit: 'contain', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}
            />
          </Box>
          {/* 缩略图横向滚动 */}
          <Box sx={{ mt: 2, px: 2, pb: 2, display: 'flex', alignItems: 'center', overflowX: 'auto', gap: 2 }}>
            {carouselThumbs.map((img, idx) => {
              const realIdx = thumbStart + idx;
              return (
                <Box
                  key={realIdx}
                  className={`rental-gallery-thumb ${realIdx === carouselIndex ? 'active' : ''}`}
                  sx={{ width: 80, height: 80, borderRadius: 2, overflow: 'hidden', border: realIdx === carouselIndex ? '2px solid #1976d2' : '2px solid transparent', cursor: 'pointer', flex: '0 0 auto' }}
                  onClick={() => setCarouselIndex(realIdx)}
                >
                  <img
                    src={img.original}
                    alt={`Thumb ${realIdx + 1}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 10 }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* YouTube Videos Section */}
        {renderVirtualTours()}

        {/* Property Overview */}
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Property Overview
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Address
              </Typography>
              <Typography variant="body1">
                {rental.address_line1}
                {rental.address_line2 && <>, {rental.address_line2}</>}
                <br />
                {rental.city}, {rental.state} {rental.zip_code}
              </Typography>
            </Box>

            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Lease Length Options
              </Typography>
              <Typography variant="body1">
                {rental.lease_lengths && Array.isArray(rental.lease_lengths) 
                  ? rental.lease_lengths.join(', ') 
                  : 'Flexible'}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                Security Deposit
              </Typography>
              <Typography variant="body1">
                {rental.security_deposit || 'Not specified'}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Description Section */}
        {rental.description && (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Description
            </Typography>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
              {rental.description}
            </Typography>
          </Paper>
        )}

        {/* Promotions Section */}
        {rental.has_promotion && rental.promotion_text && (
          <Paper elevation={1} sx={{ p: 3, mb: 3, bgcolor: '#fff3e0' }}>
            <Typography variant="h6" gutterBottom color="primary">
              Special Promotion
            </Typography>
            <Typography variant="body1">
              {rental.promotion_text}
            </Typography>
      </Paper>
        )}

        {/* Available Rooms/Units/Beds Section or Property Detail */}
        {isApartmentEntire ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Available Units
            </Typography>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left">Unit</TableCell>
                    <TableCell align="center">Bedrooms</TableCell>
                    <TableCell align="center">Bathrooms</TableCell>
                    <TableCell align="center">Sq Ft</TableCell>
                    <TableCell align="center">Price</TableCell>
                    <TableCell align="center">Availability</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rental.units && rental.units.length > 0 ? (
                    rental.units.map((unit, idx) => (
                      <TableRow key={unit.id || unit.unitNumber || idx}>
                        <TableCell align="left">{unit.unitNumber}</TableCell>
                        <TableCell align="center">{formatBedroomsLabel(unit.bedrooms)}</TableCell>
                        <TableCell align="center">{formatBathrooms(unit.bathrooms)}</TableCell>
                        <TableCell align="center">{unit.squareFeet || '-'}</TableCell>
                        <TableCell align="center">${unit.price}</TableCell>
                        <TableCell align="center">{formatAvailabilityDate(unit.availableDate)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={6} align="center">No data</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : isSpecialEntire ? (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Property Detail
            </Typography>
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="left" sx={{ width: '20%' }}>Bedrooms</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Bathrooms</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Sq Ft</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Price</TableCell>
                    <TableCell align="left" sx={{ width: '20%' }}>Available Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell align="left">{formatBedroomsLabel(rental.bedrooms)}</TableCell>
                    <TableCell align="left">{formatBathrooms(rental.bathrooms)}</TableCell>
                    <TableCell align="left">{rental.square_feet || '-'}</TableCell>
                    <TableCell align="left">{'$' + (Number(rental.price) % 1 === 0 ? parseInt(rental.price) : rental.price)}</TableCell>
                    <TableCell align="left">{formatAvailabilityDate(rental.available_date)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ) : (
          <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              {rental.rental_type === 'entire' ? 'Available Units' :
               rental.rental_type === 'room' ? 'Available Rooms' :
               'Available Beds'}
            </Typography>
            
            <TableContainer>
              <Table sx={{ minWidth: 650 }}>
                <TableHead>
                  <TableRow>
                    <TableCell align="center">{isUnit ? 'Unit' : isRoom ? 'Room' : 'Bed'}</TableCell>
                    <TableCell align="center">Base Price</TableCell>
                    {showSqft && <TableCell align="center">Sq Ft</TableCell>}
                    {showBathroom && <TableCell align="center">Bathroom</TableCell>}
                    <TableCell align="center">Availability</TableCell>
                    {(isRoom || isBed) && <TableCell align="center">Unit Type</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tableData && tableData.length > 0 ? (
                    tableData.map((item, idx) => (
                      <TableRow key={item.id || item.unitNumber || item.roomNumber || item.bedNumber || idx}>
                        <TableCell align="center">{getNumber(item)}</TableCell>
                        <TableCell align="center">${item.price}</TableCell>
                        {showSqft && <TableCell align="center">{item.squareFeet || '-'}</TableCell>}
                        {showBathroom && <TableCell align="center">{item.hasBathroom ? 'Private' : 'Shared'}</TableCell>}
                        <TableCell align="center">{formatAvailabilityDate(item.availableDate)}</TableCell>
                        {(isRoom || isBed) && (
                          rental && ['House', 'Townhouse', 'Condo'].includes(rental.property_type)
                            ? (
                                tableData.length > 1
                                  ? (idx === 0
                                      ? <TableCell align="center" rowSpan={tableData.length} sx={{ verticalAlign: 'middle', fontWeight: 600, fontSize: '1.1rem' }}>{formatBedroomBathroom(rental.bedrooms, rental.bathrooms)}</TableCell>
                                      : null)
                                  : <TableCell align="center" sx={{ verticalAlign: 'middle', fontWeight: 600, fontSize: '1.1rem' }}>{formatBedroomBathroom(rental.bedrooms, rental.bathrooms)}</TableCell>
                              )
                            : <TableCell align="center">{hasBedroomAndBathroomValues(item.bedrooms, item.bathrooms) ? formatBedroomBathroom(item.bedrooms, item.bathrooms) : '-'}</TableCell>
                        )}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell align="center" colSpan={colCount + ((isRoom || isBed) ? 1 : 0)}>
                        No data
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        )}

      {/* Amenities */}
        {rental.amenities && Array.isArray(rental.amenities) && rental.amenities.length > 0 && (
        <Paper elevation={1} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h5" gutterBottom>
            Amenities
          </Typography>
          <Grid container spacing={2}>
            {rental.amenities.map((amenity, index) => (
              <Grid item xs={12} sm={6} md={4} key={index}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircleIcon color="primary" />
                  <Typography>{amenity}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

        {/* 地图展示（移到Amenities后） */}
        {rental.latitude && rental.longitude && (
          <Box sx={{ height: 400, my: 4 }}>
            <MapView 
              centerLat={rental.latitude}
              centerLng={rental.longitude}
              markers={[{ id: rental.id, lat: rental.latitude, lng: rental.longitude }]}
              markerData={{ [rental.id]: rental }}
              allowBoundsChange={false}
              uscPolygon={USC_DPS_POLYGON}
            />
          </Box>
        )}
          </Grid>
          {renderContactSidebar()}
        </Grid>

      {/* Contact Form Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Contact Property Owner
          <IconButton
            aria-label="close"
            onClick={handleCloseContactDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Full Name *
              </Typography>
              <input
                style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', backgroundColor: '#f5f5f5' }}
                name="name"
                type="text"
                value={contactForm.name}
                onChange={handleContactInputChange}
                placeholder="Enter your full name"
                autoComplete="new-password"
                required
                disabled
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Name is automatically filled from your account and cannot be changed
              </Typography>
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Email *
              </Typography>
              <input
                style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactInputChange}
                placeholder="Enter your email address"
                autoComplete="new-password"
                required
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Phone Number (Optional)
              </Typography>
              <input
                style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box' }}
                name="phone"
                type="text"
                value={contactForm.phone}
                onChange={handleContactInputChange}
                placeholder="Enter your phone number"
                autoComplete="new-password"
              />
            </Box>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Message *
              </Typography>
              <textarea
                style={{ width: '100%', padding: 12, fontSize: 16, border: '1px solid #ccc', borderRadius: 4, boxSizing: 'border-box', minHeight: 80 }}
                name="message"
                value={contactForm.message}
                onChange={handleContactInputChange}
                placeholder="Write your message here..."
                required
              />
            </Box>
            
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Your message will be sent to the property owner through our secure system.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseContactDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitContact} 
            variant="contained"
            disabled={!contactForm.name || !contactForm.email || !contactForm.message}
          >
            Send Message
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
  }
  return null;
}

export default RentalDetail; 